import { CircleCheck, Copy, LayoutTemplate, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field, FieldRow } from '../../../components/Field';
import { Popover, usePopover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useModule } from '../../../lib/modules';
import { useToasts } from '../../../stores/toasts';
import { useChangeJobConfig, useJobs, useJobTemplates, useUndoJobCopy, type ConfigChange } from '../api/jobs-api';
import shortcutStyles from './SetupShortcuts.module.css';
import styles from './steps.module.css';

/**
 * F5.02 and F5.03 — fill the whole setup in one go, at the top of JOB-13 (R3). A template or an
 * earlier job is COPIED in (non-negotiable #4); neither is read again. Certified templates come
 * first, with a badge (F5.23). Copying last month's job also brings its people and files
 * (DECISIONS-LOG "Start R3" Q3), never its candidates.
 *
 * Undo beats confirm (CLAUDE.md §5a rule 8): applying just happens, and the toast
 * puts back exactly what was there. A copy keeps its undo longer (workflow review C1,
 * ADR 0076): "Undo the copy" stays on this card until the job opens. Only offered before
 * any candidate has applied — after that a job changes one step at a time.
 *
 * Role templates are a module (§L34): while it is off the template picker isn't drawn and the
 * card is only "Copy a previous job".
 */
export function SetupShortcuts({
  job,
  onReplaced,
}: {
  readonly job: JobDetail;
  /** The job after a template, copy or undo landed — step 1 reloads its fields from it. */
  readonly onReplaced: (job: JobDetail) => void;
}): ReactNode {
  const isTemplatesOn = useModule('templates');
  const templates = useJobTemplates(job.id);
  const jobs = useJobs({ page: 1, pageSize: 100 });
  const change = useChangeJobConfig();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const [templateId, setTemplateId] = useState<string | null>(job.roleTemplateId);
  const [fromJobId, setFromJobId] = useState<string | null>(null);

  async function apply(next: ConfigChange, done: string): Promise<void> {
    try {
      const result = await change.mutateAsync({ id: job.id, version: job.version, change: next });
      onReplaced(result.job);
      // ADR 0076: a copy's undo lives on the card until the job opens, so its toast needs no button.
      if (next.kind === 'copy') {
        show({ tone: 'success', message: done, detail: 'You can undo the copy until the job opens.' });
        return;
      }
      showUndo(done, () => {
        change
          .mutateAsync({ id: job.id, version: result.job.version, change: { kind: 'restore', config: result.previous } })
          .then((restored) => onReplaced(restored.job))
          .catch((cause: unknown) =>
            show({ tone: 'danger', message: 'The earlier setup was not put back', detail: errorMessage(cause) }),
          );
      });
    } catch (cause) {
      show({ tone: 'danger', message: 'Nothing was changed', detail: errorMessage(cause) });
    }
  }

  const otherJobs = (jobs.data?.rows ?? []).filter((row) => row.id !== job.id);
  const templateName = templates.data?.find((template) => template.id === templateId)?.name;

  return (
    <Card
      title={isTemplatesOn ? 'Start from a template or a previous job' : 'Start from a previous job'}
      meta="Fills in every section. A one-time copy"
    >
      <FieldRow>
        {isTemplatesOn && (
          <Field label="Start from template">
            {({ inputId }) => (
              <div className={styles.inline}>
                <Select
                  id={inputId}
                  label="Role template"
                  placeholder={templates.data?.length === 0 ? 'No templates for this department' : 'Choose a template'}
                  value={templateId}
                  options={(templates.data ?? []).map((template) => ({
                    value: template.id,
                    label: template.name,
                    ...(template.roleFamily ? { description: template.roleFamily } : {}),
                    ...(template.isCertified ? { badge: 'Certified' } : {}),
                  }))}
                  onChange={setTemplateId}
                />
                <Button
                  variant="outline"
                  icon={LayoutTemplate}
                  disabled={templateId === null}
                  isPending={change.isPending}
                  onClick={() =>
                    templateId &&
                    void apply({ kind: 'template', roleTemplateId: templateId }, `${templateName ?? 'Template'} applied`)
                  }
                >
                  Apply
                </Button>
              </div>
            )}
          </Field>
        )}
        <Field label="Copy a previous job">
          {({ inputId }) => (
            <div className={styles.inline}>
              <Select
                id={inputId}
                label="Previous job"
                placeholder="Choose a job"
                value={fromJobId}
                options={otherJobs.map((row) => ({
                  value: row.id,
                  label: row.publicTitle ?? row.title,
                  description: `${row.reference} · ${row.departmentName}`,
                }))}
                onChange={setFromJobId}
              />
              <Button
                variant="outline"
                icon={Copy}
                disabled={fromJobId === null}
                isPending={change.isPending}
                onClick={() => fromJobId && void apply({ kind: 'copy', fromJobId }, 'Setup copied')}
              >
                Copy
              </Button>
            </div>
          )}
        </Field>
      </FieldRow>
      {job.undoableCopy && <UndoCopy job={job} copy={job.undoableCopy} onReplaced={onReplaced} />}
    </Card>
  );
}

/**
 * C1 (ADR 0076): where the setup came from, and the way back. Undoing also drops what was changed since the copy,
 * which cannot be got back, so it asks once — in a pop-up beside the button, never mid-screen (CLAUDE.md §5).
 */
function UndoCopy({
  job,
  copy,
  onReplaced,
}: {
  readonly job: JobDetail;
  readonly copy: NonNullable<JobDetail['undoableCopy']>;
  readonly onReplaced: (job: JobDetail) => void;
}): ReactNode {
  const undo = useUndoJobCopy();
  const show = useToasts((state) => state.show);
  const { triggerRef, anchor, isOpen, toggle, close } = usePopover();

  async function run(): Promise<void> {
    close();
    try {
      const restored = await undo.mutateAsync({ id: job.id, version: job.version });
      onReplaced(restored);
      show({ tone: 'success', message: 'Copy undone', detail: 'The setup from before the copy is back.' });
    } catch (cause) {
      show({ tone: 'danger', message: 'The copy was not undone', detail: errorMessage(cause) });
    }
  }

  return (
    <div className={shortcutStyles.copied}>
      <span className={shortcutStyles.copiedText}>
        <CircleCheck size={16} aria-hidden className={shortcutStyles.copiedIcon} />
        <span>
          Copied from <b>{copy.fromTitle}</b> · {copy.fromReference}
        </span>
      </span>
      <Button ref={triggerRef} size="sm" variant="outline" icon={Undo2} isPending={undo.isPending} aria-haspopup="dialog" aria-expanded={isOpen} onClick={toggle}>
        Undo the copy
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label="Undo the copy">
        <div className={shortcutStyles.confirm}>
          <strong>Put back the setup from before the copy?</strong>
          <span>Changes made since the copy are undone too.</span>
          <div className={shortcutStyles.confirmActions}>
            <Button size="sm" variant="solid" onClick={() => void run()}>
              Undo the copy
            </Button>
            <Button size="sm" onClick={close}>
              Keep it
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
